/**
 * Centralized runtime configuration module.
 *
 * Single provider-agnostic entry point (research R7). Loads `window.__RUNTIME_CONFIG__`
 * (falling back to import.meta.env.VITE_* for local vite dev), validates fail-fast, and
 * exposes typed accessors used by auth, the API client, and pages.
 *
 * Usage:
 *   import { getConfig, resolveApiBaseUrl } from '@/config';
 */

import type {
  RuntimeConfig,
  Environment,
  AuthMode,
  OidcSettings,
  MockUser,
  MonitoredService,
  OidcClientConfig,
  ValidationResult,
} from './types';
import { loadRuntimeConfig } from './loader';
import { validateConfig, isValidUrl, isLocalhostUrl } from './validation';
import { ConfigError, ConfigValidationError } from './errors';
import { DEV_PROXY_PATH_PREFIX } from './proxy';

// Re-exports
export type {
  RuntimeConfig,
  Environment,
  AuthMode,
  OidcSettings,
  MockUser,
  MonitoredService,
  OidcClientConfig,
  ValidationResult,
};
export { validateConfig, isValidUrl, isLocalhostUrl };
export { ConfigError, ConfigValidationError };
export { loadRuntimeConfig };

/** localStorage key for the local-only dev API-target override (data-model: retained from vibe). */
const DEV_API_OVERRIDE_KEY = 'app:dev-api-target';

let _config: RuntimeConfig | null = null;

/**
 * Get the validated runtime configuration (singleton).
 *
 * @throws {ConfigValidationError} if the loaded configuration is invalid.
 */
export function getConfig(): RuntimeConfig {
  if (_config === null) {
    const loaded = loadRuntimeConfig();
    const result = validateConfig(loaded);
    if (!result.valid) {
      throw new ConfigValidationError(result.errors);
    }
    _config = loaded;
  }
  return _config;
}

/** Reset the cached config (test-only helper). */
export function __resetConfigCache(): void {
  _config = null;
}

/** True when authenticating against the mock provider (local development). */
export function isMockMode(): boolean {
  try {
    return getConfig().authMode === 'mock';
  } catch {
    return false;
  }
}

/** True when the local dev API-target override is applicable (environment === 'dev'). */
export function isDevApiOverrideAvailable(): boolean {
  try {
    return getConfig().environment === 'dev';
  } catch {
    return false;
  }
}

/** Read the local dev API-target override key, if set and applicable. */
export function getDevApiOverride(): string | null {
  if (!isDevApiOverrideAvailable()) return null;
  try {
    return localStorage.getItem(DEV_API_OVERRIDE_KEY);
  } catch {
    return null;
  }
}

/** Set (or clear, with null) the local dev API-target override key. */
export function setDevApiOverride(key: string | null): void {
  try {
    if (key === null) localStorage.removeItem(DEV_API_OVERRIDE_KEY);
    else localStorage.setItem(DEV_API_OVERRIDE_KEY, key);
  } catch {
    // ignore storage errors
  }
}

/** All configured backend target keys. */
export function getApiTargetKeys(): string[] {
  return Object.keys(getConfig().apiBaseUrls);
}

/** The effective default target key: dev override (if valid) else configured defaultApiKey. */
export function getActiveApiKey(): string {
  const config = getConfig();
  const override = getDevApiOverride();
  if (override && override in config.apiBaseUrls) {
    return override;
  }
  return config.defaultApiKey;
}

/**
 * Resolve a backend base URL from an optional target key.
 * Falls back to the active/default key when target is omitted (research R5).
 *
 * When the local-dev CORS proxy is enabled (config.devProxy), returns the reserved same-origin
 * prefix `${DEV_PROXY_PATH_PREFIX}/<key>` instead of the absolute origin, so the browser makes a
 * same-origin request that the vite dev server forwards to the real backend (feature 002).
 *
 * @throws {ConfigError} if the requested target key is not configured.
 */
export function resolveApiBaseUrl(target?: string): string {
  const config = getConfig();
  const key = target ?? getActiveApiKey();
  const baseUrl = config.apiBaseUrls[key];
  if (!baseUrl) {
    throw new ConfigError(
      `Unknown backend target "${key}". Configured targets: ${getApiTargetKeys().join(', ') || 'none'}.`
    );
  }
  if (config.devProxy) {
    return `${DEV_PROXY_PATH_PREFIX}/${key}`;
  }
  return baseUrl;
}

/** Default liveness dashboard auto-refresh cadence (ms) when not configured (FR-005). */
const DEFAULT_LIVENESS_REFRESH_INTERVAL_MS = 30000;

/** Humanize an apiBaseUrls key into a display name, e.g. "metadata-stripper" → "Metadata Stripper". */
function humanizeKey(key: string): string {
  return key
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * The monitored services for the liveness dashboard, in configured order.
 *
 * When `services` is present in config it is returned as-is (validated fail-fast at load).
 * When absent, derive one service per apiBaseUrls entry (id = target = key, displayName =
 * humanized key) per data-model.md.
 */
export function getMonitoredServices(): MonitoredService[] {
  const config = getConfig();
  if (config.services && config.services.length > 0) {
    return config.services;
  }
  return Object.keys(config.apiBaseUrls).map((key) => ({
    id: key,
    displayName: humanizeKey(key),
    target: key,
  }));
}

/** The dashboard auto-refresh cadence in ms (configured value or the 30s default). */
export function getLivenessRefreshIntervalMs(): number {
  return getConfig().livenessRefreshIntervalMs ?? DEFAULT_LIVENESS_REFRESH_INTERVAL_MS;
}

/**
 * The backend target key the API Route Explorer uses (feature 003, FR-012).
 * Returns the configured `apiExplorerTarget` when set, else the active/default key.
 */
export function getApiExplorerTarget(): string {
  return getConfig().apiExplorerTarget ?? getActiveApiKey();
}

/**
 * Build the oidc-client-ts client configuration from the runtime OIDC settings.
 * Returns null in mock mode (no OIDC).
 */
export function getOidcClientConfig(): OidcClientConfig | null {
  const config = getConfig();
  if (config.authMode !== 'oidc' || !config.oidc) {
    return null;
  }
  const oidc = config.oidc;
  return {
    authority: oidc.authority,
    client_id: oidc.clientId,
    redirect_uri: oidc.redirectUri,
    post_logout_redirect_uri: oidc.postLogoutRedirectUri,
    response_type: 'code',
    scope: oidc.scope,
    loadUserInfo: true,
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: 300,
  };
}
